/** Account section inside settings: name, sign-in method, marketing opt-in. */
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '@/features/auth/useAuth';
import { useT } from '@/i18n/useT';
import {
  ONBOARDING_DISPLAY_NAME_INPUT_BUFFER,
  ONBOARDING_DISPLAY_NAME_MAX_LENGTH,
} from '@/features/onboarding/utils/onboardingDisplayNameUtils';
import {
  SETTINGS_ACCOUNT_SECTION_TEST_ID,
  SETTINGS_DISPLAY_NAME_INPUT_TEST_ID,
  SETTINGS_DISPLAY_NAME_SAVE_TEST_ID,
  SETTINGS_MARKETING_OPT_IN_TEST_ID,
} from '@/features/settings/constants/settingsTestIds';
import AccountDeleteDialogs from '@/features/settings/components/AccountDeleteDialogs';
import { useAccountSettings } from '@/features/settings/hooks/useAccountSettings';
import { useAccountDelete } from '@/features/settings/hooks/useAccountDelete';
import {
  isAccountEmailVerified,
  resolveAccountSignInMethod,
} from '@/features/settings/utils/accountSignInMethod';

export default function AccountSettingsSection() {
  const t = useT();
  const { user } = useAuth();
  const {
    canEdit,
    displayName,
    setDisplayName,
    displayNameErrorKey,
    displayNameSaving,
    saveDisplayName,
    marketingOptIn,
    marketingSaving,
    marketingErrorKey,
    toggleMarketingOptIn,
  } = useAccountSettings();

  const {
    confirmOpen,
    reauthPasswordOpen,
    reauthPassword,
    setReauthPassword,
    deleteErrorKey,
    deleteInProgress,
    beginAccountDelete,
    submitReauthPassword,
    confirmAccountDelete,
    cancelAccountDelete,
  } = useAccountDelete({ user });

  if (!canEdit) {
    return null;
  }

  const signInMethod = resolveAccountSignInMethod(user);
  const providerLabelKey =
    signInMethod === 'google'
      ? 'settings.account.signInMethod.google'
      : signInMethod === 'password'
        ? 'settings.account.signInMethod.password'
        : 'settings.account.signInMethod.unknown';

  const email = user?.email ?? '';
  const emailVerified = isAccountEmailVerified(user);
  const statusAlertKey =
    displayNameErrorKey ?? marketingErrorKey ?? deleteErrorKey;

  return (
    <Box
      component="section"
      data-testid={SETTINGS_ACCOUNT_SECTION_TEST_ID}
      aria-labelledby="settings-account-heading"
    >
      <Typography id="settings-account-heading" variant="h6" component="h2" gutterBottom>
        {t('settings.account.title', 'Account')}
      </Typography>

      {statusAlertKey ? (
        <Typography
          role="alert"
          aria-live="polite"
          variant="body2"
          color="error"
          sx={{ mb: 1 }}
        >
          {t(statusAlertKey)}
        </Typography>
      ) : null}

      <TextField
        fullWidth
        size="small"
        label={t('settings.account.displayName.label', 'Your name')}
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        disabled={displayNameSaving}
        error={Boolean(displayNameErrorKey)}
        inputProps={{
          'data-testid': SETTINGS_DISPLAY_NAME_INPUT_TEST_ID,
          maxLength:
            ONBOARDING_DISPLAY_NAME_MAX_LENGTH +
            ONBOARDING_DISPLAY_NAME_INPUT_BUFFER,
        }}
        sx={{ mb: 1 }}
      />
      <Button
        variant="outlined"
        size="small"
        disabled={displayNameSaving}
        data-testid={SETTINGS_DISPLAY_NAME_SAVE_TEST_ID}
        onClick={() => void saveDisplayName()}
      >
        {t('settings.account.displayName.save', 'Save name')}
      </Button>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" component="h3">
          {t('settings.account.signInMethod.title', 'Sign-in method')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(providerLabelKey)}
        </Typography>
        {email ? (
          <Typography variant="body2" color="text.secondary">
            {email}
            {signInMethod === 'password' ? (
              <>
                {' — '}
                {emailVerified
                  ? t('settings.account.signInMethod.emailVerified', 'Verified')
                  : t(
                      'settings.account.signInMethod.emailUnverified',
                      'Not verified yet',
                    )}
              </>
            ) : null}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ mt: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={marketingOptIn}
              disabled={marketingSaving}
              onChange={(e) => void toggleMarketingOptIn(e.target.checked)}
              slotProps={{
                input: {
                  'data-testid': SETTINGS_MARKETING_OPT_IN_TEST_ID,
                },
              }}
            />
          }
          label={t(
            'settings.account.marketing.label',
            'News and updates about Riposo',
          )}
        />
        <Typography variant="body2" color="text.secondary">
          {t(
            'settings.account.marketing.description',
            'Occasional product news. You can turn this off anytime.',
          )}
        </Typography>
      </Box>

      <AccountDeleteDialogs
        confirmOpen={confirmOpen}
        reauthPasswordOpen={reauthPasswordOpen}
        reauthPassword={reauthPassword}
        onReauthPasswordChange={setReauthPassword}
        deleteInProgress={deleteInProgress}
        onBeginDelete={beginAccountDelete}
        onSubmitReauthPassword={submitReauthPassword}
        onConfirmDelete={confirmAccountDelete}
        onCancel={cancelAccountDelete}
      />
    </Box>
  );
}
